/**
 * AdminAvatar — adds a rotating sparkle border + shimmer sheen to a
 * user's profile picture. Used in the Navbar (and anywhere else we
 * surface the signed-in user's avatar) when `user.role === "admin"`
 * so admin accounts stand out at a glance.
 *
 * Layers, bottom → top:
 *   1. Rotating conic-gradient ring   (amber/gold sweep, 4s loop)
 *   2. White ring "donut hole"        (so the avatar reads cleanly)
 *   3. <img> avatar                   (object-cover, circular)
 *   4. Shimmer sheen overlay          (diagonal white gradient, 3s loop)
 *   5. Four static-position sparkles  (twinkle, staggered delays)
 *
 * All motion is CSS-only — no JS, no re-render churn. Respects
 * `prefers-reduced-motion` via the global `.no-motion` class set by
 * SplashIntro / a11y settings (those styles already exist in
 * index.css and disable `animation` on every `*` descendant).
 *
 * Props:
 *   src       string  — user.picture
 *   alt       string  — user.name
 *   size      number  — avatar diameter in px (default 28)
 *   className string  — passes through to the outermost <span>
 */
import React from "react";

export default function AdminAvatar({ src, alt, size = 28, className = "" }) {
  // The rotating ring needs ~3px of breathing room around the avatar
  // so the sparkle stays visible. Total wrapper is size + 6.
  const wrapper = size + 6;
  return (
    <span
      className={`relative inline-block ${className}`}
      style={{ width: wrapper, height: wrapper }}
      data-testid="admin-avatar"
      aria-label="Admin"
    >
      {/* 1. Rotating conic ring */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full admin-avatar-ring"
      />
      {/* 2. White inset donut hole — keeps the avatar from blending into the ring */}
      <span
        aria-hidden
        className="absolute inset-[2px] rounded-full bg-white"
      />
      {/* 3. Avatar image */}
      {src ? (
        <img
          src={src}
          alt={alt || "Admin"}
          className="absolute inset-[3px] rounded-full object-cover"
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          className="absolute inset-[3px] rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-white font-extrabold grid place-items-center"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
        >
          {(alt || "A").slice(0, 1).toUpperCase()}
        </span>
      )}
      {/* 4. Shimmer sheen overlay */}
      <span
        aria-hidden
        className="absolute inset-[3px] rounded-full overflow-hidden pointer-events-none"
      >
        <span className="absolute inset-0 admin-avatar-shimmer" />
      </span>
      {/* 5. Sparkles */}
      <Sparkle style={{ top: -2, left: "50%", marginLeft: -3, animationDelay: "0s" }} />
      <Sparkle style={{ right: -2, top: "50%", marginTop: -3, animationDelay: "0.7s" }} />
      <Sparkle style={{ bottom: -2, left: "50%", marginLeft: -3, animationDelay: "1.4s" }} />
      <Sparkle style={{ left: -2, top: "50%", marginTop: -3, animationDelay: "2.1s" }} />
    </span>
  );
}

function Sparkle({ style }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 12 12"
      width={7}
      height={7}
      className="absolute admin-avatar-sparkle"
      style={style}
    >
      <path
        d="M6 0 L7 5 L12 6 L7 7 L6 12 L5 7 L0 6 L5 5 Z"
        fill="#fbbf24"
      />
    </svg>
  );
}
